import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'

import { makeStyles } from 'tss-react/mui'

import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'

import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'

import {
    ActionsRowResponsive,
    buttonSx,
    chipSx,
    classToFComponent,
    EditorBody,
    EditorSideColumn,
    FCClassContext,
    LifesHubEmptyState,
    PanelCard,
    PanelCardBody,
    SkeletonBlock
} from 'src/utils/views'
import { formatCompetencia, formatCurrency } from 'src/utils/formatter-utils'

import PriceTierEditor from '../../billing/views/bl_price_tier_editor'
import type { PriceTierInput } from '../../billing/bl_types'
import { BillingConfigScope } from '../scopes'
import { Banner, CardHead, SettingsPanel, SettingsStack } from 'src/utils/views/modules/admin-configurations'

const LOG = Logger.get('BillingConfig-VIEW')

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        flexGrow: 1
    },
    tierPanel: {
        '& .MuiOutlinedInput-root': {
            minHeight: 40,
            borderRadius: 9,
            backgroundColor: '#fff',
            fontSize: 13.5,
            '& fieldset': { borderColor: 'var(--border-strong)' },
            '&:hover fieldset': { borderColor: '#aebbb3' },
            '&.Mui-focused fieldset': { borderColor: 'var(--green-700)', borderWidth: 1 },
            '&.Mui-focused': { boxShadow: 'rgba(38,229,31,.12) 0 0 0 3px' }
        },
        '& .MuiButton-root': { textTransform: 'none', fontWeight: 650, color: 'var(--green-800)' },
        '& .MuiAlert-root': {
            alignItems: 'flex-start',
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid #f3d6a3',
            backgroundColor: 'var(--warning-bg)',
            color: '#6d4605',
            fontSize: 13
        },
        '& .MuiAlert-icon': { color: '#6d4605', paddingTop: 2 },
        '& .MuiTypography-caption': { fontSize: 12, fontWeight: 700, color: '#303733' },
        '& .MuiTypography-root:not(.MuiTypography-caption)': { color: 'var(--muted)' }
    },
    tableWrap: {
        width: '100%',
        overflowX: 'auto',
        '& table': {
            width: '100%',
            borderCollapse: 'collapse'
        },
        '& th': {
            padding: '13px 12px',
            color: '#2a302c',
            backgroundColor: '#fbfcfb',
            fontSize: 11.5,
            fontWeight: 700,
            textAlign: 'left',
            whiteSpace: 'nowrap',
            borderBottom: '1px solid var(--border)'
        },
        '& td': {
            padding: '12px',
            verticalAlign: 'middle',
            fontSize: 12,
            borderBottom: '1px solid var(--border)'
        },
        '& tbody tr:last-child td': { borderBottom: 0 },
        '& tbody tr:hover': { background: 'linear-gradient(90deg, var(--green-050), #fbfffb)' },
        '@media (max-width: 720px)': {
            overflow: 'visible',
            '& table, & thead, & tbody, & th, & td, & tr': { display: 'block' },
            '& thead': { display: 'none' },
            '& tbody': { display: 'grid', gap: 12 },
            '& tr': {
                border: '1px solid var(--border)',
                borderRadius: 11,
                backgroundColor: '#fff',
                overflow: 'hidden'
            },
            '& td': {
                display: 'grid',
                gridTemplateColumns: 'minmax(105px, .7fr) 1fr',
                gap: 12,
                padding: '10px 12px',
                borderBottom: '1px dashed #e5e9e6'
            },
            '& td::before': {
                content: 'attr(data-label)',
                color: 'var(--muted)',
                fontSize: 11,
                fontWeight: 750
            },
            '& td:first-of-type': { display: 'block', padding: '14px 12px' },
            '& td:first-of-type::before': { display: 'none' },
            '& tbody tr td:last-of-type': { borderBottom: 0 }
        }
    },
    tierList: {
        color: 'var(--muted)',
        fontSize: 12.5,
        overflowWrap: 'anywhere'
    }
})

type BillingConfigViewProps = {
    className?: string
    scope: BillingConfigScope
}

class BillingConfigViewClass implements FCClassContext<BillingConfigViewProps> {
    scope!: BillingConfigScope
    sytles!: ReturnType<typeof useStyles>

    readonly emitSave = () => this.scope.onSave().catch(LOG.caught)

    onSyncState({ scope }: BillingConfigViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: BillingConfigViewProps) {
        this.scope = scope
        const { classes } = this.sytles

        if (scope.waiting) {
            return (
                <div className={clsx(classes.view, className)}>
                    <EditorBody>
                        <SettingsStack>
                            <SkeletonBlock sx={{ height: 320 }} />
                            <SkeletonBlock sx={{ height: 220 }} />
                        </SettingsStack>
                        <EditorSideColumn>
                            <SkeletonBlock sx={{ height: 200 }} />
                        </EditorSideColumn>
                    </EditorBody>
                </div>
            )
        }

        return (
            <div className={clsx(classes.view, className)}>
                <EditorBody>
                    <SettingsStack>
                        {this.renderTiersCard()}
                        {this.renderHistoryCard()}
                    </SettingsStack>
                    <EditorSideColumn>{this.renderCurrentCard()}</EditorSideColumn>
                </EditorBody>
            </div>
        )
    }

    renderTiersCard() {
        const { classes } = this.sytles
        const { scope } = this

        return (
            <PanelCard elevation={0}>
                <CardHead>
                    <div>
                        <h3>Contrato padrão</h3>
                        <p className="subtitle">Preço por registro excedente cobrado de quem não tem tabela própria.</p>
                    </div>
                </CardHead>
                <PanelCardBody>
                    <Banner tone="blue">
                        Estes preços valem para todos os grupos que não possuem tabela própria cadastrada em
                        Administração :: Grupos.
                    </Banner>

                    <SettingsPanel className={classes.tierPanel}>
                        <PriceTierEditor scope={scope.tierEditor} />
                    </SettingsPanel>

                    <ActionsRowResponsive>
                        <Button
                            sx={buttonSx('primary')}
                            startIcon={<SaveOutlinedIcon />}
                            onClick={this.emitSave}
                            disabled={scope.tierEditor.errors.length > 0}
                        >
                            Salvar tabela
                        </Button>
                    </ActionsRowResponsive>
                </PanelCardBody>
            </PanelCard>
        )
    }

    renderHistoryCard() {
        const { classes } = this.sytles
        const { scope } = this

        return (
            <PanelCard elevation={0}>
                <CardHead>
                    <div>
                        <h3>Histórico de vigências</h3>
                        <p className="subtitle">
                            Salvar cria uma vigência nova; as competências já apuradas continuam usando a tabela da
                            época.
                        </p>
                    </div>
                </CardHead>
                {scope.history.length === 0 ? (
                    <LifesHubEmptyState
                        title="Nenhuma vigência registrada"
                        text="A primeira vigência é criada quando você salvar a tabela de preços acima."
                    />
                ) : (
                    <PanelCardBody>
                        <div className={classes.tableWrap}>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Vigente a partir de</th>
                                        <th>Vigente até</th>
                                        <th>Faixas</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scope.history.map((version) => (
                                        <tr key={version.id}>
                                            <td data-label="Vigente a partir de" style={{ fontWeight: 750 }}>
                                                {formatCompetencia(version.validFrom)}
                                            </td>
                                            <td data-label="Vigente até">
                                                {version.validTo ? (
                                                    formatCompetencia(version.validTo)
                                                ) : (
                                                    <Chip size="small" label="Em vigor" sx={chipSx('green')} />
                                                )}
                                            </td>
                                            <td data-label="Faixas" className={classes.tierList}>
                                                {describeTiers(version.tiers)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </PanelCardBody>
                )}
            </PanelCard>
        )
    }

    renderCurrentCard() {
        const { classes } = this.sytles
        const { scope } = this

        return (
            <PanelCard elevation={0}>
                <CardHead>
                    <div>
                        <h3>Faixas em edição</h3>
                        <p className="subtitle">O excedente do mês cai em uma única faixa.</p>
                    </div>
                </CardHead>
                <PanelCardBody>
                    {scope.tierEditor.tiers.length === 0 ? (
                        <div className={classes.tierList}>Nenhuma faixa definida.</div>
                    ) : (
                        <div className={classes.tableWrap}>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Excedente</th>
                                        <th>Preço unitário</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scope.tierEditor.tiers.map((tier, index) => (
                                        <tr key={`${tier.minQty}-${index}`}>
                                            <td data-label="Excedente">{describeRange(tier)}</td>
                                            <td data-label="Preço unitário" style={{ fontWeight: 650 }}>
                                                {formatCurrency(tier.unitPrice)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </PanelCardBody>
            </PanelCard>
        )
    }
}

export const BillingConfigView = classToFComponent(BillingConfigViewClass, React)
export default BillingConfigView

function describeRange(tier: PriceTierInput): string {
    return tier.maxQty === null || tier.maxQty === undefined
        ? `${tier.minQty} ou mais`
        : `${tier.minQty} a ${tier.maxQty}`
}

function describeTiers(tiers: PriceTierInput[]): string {
    if (!tiers || tiers.length === 0) {
        return '—'
    }

    return tiers
        .map((tier) => {
            const range = tier.maxQty === null ? `${tier.minQty}+` : `${tier.minQty}-${tier.maxQty}`
            return `${range}: ${formatCurrency(tier.unitPrice)}`
        })
        .join(' | ')
}
